import { LAYER_ENVIRONMENTS } from '../../layerEnvironments'
import { LabeledField } from '../LabeledField'
import {
  type DemoFormChange,
  type DemoFormFieldsProps,
  type DemoFormValues,
} from './types'

type TextField = {
  type: 'text'
  key: Extract<
    keyof DemoFormValues,
    | 'brandColorHex'
    | 'brandColorLightHex'
    | 'brandColorDarkHex'
    | 'brandColorTextHex'
    | 'brandLogoUrl'
    | 'businessId'
  >
  label: string
  required?: boolean
}

type CheckboxField = {
  type: 'checkbox'
  key: Extract<
    keyof DemoFormValues,
    | 'bookkeepingOn'
    | 'showDescriptions'
    | 'showReceiptUploads'
    | 'showInvoicesTab'
    | 'showCustomerVendor'
    | 'defaultToMobile'
  >
  label: string
}

type SelectKey = 'datePickerMode' | 'layerEnvironmentOverride'

type SelectField = {
  [K in SelectKey]: {
    type: 'select'
    key: K
    label: string
    options: Array<{ value: NonNullable<DemoFormValues[K]> | ''; label: string }>
  }
}[SelectKey]

type FieldDef = TextField | CheckboxField | SelectField

const FIELDS: FieldDef[] = [
  { type: 'text', key: 'brandColorHex', label: 'Brand Primary Color (Hex):', required: true },
  { type: 'text', key: 'brandColorLightHex', label: 'Brand Light Color (Hex):', required: true },
  { type: 'text', key: 'brandColorDarkHex', label: 'Brand Dark Color (Hex):', required: true },
  { type: 'text', key: 'brandColorTextHex', label: 'Brand Text Color (Hex):', required: true },
  { type: 'text', key: 'brandLogoUrl', label: 'Brand Logo URL:', required: true },
  { type: 'text', key: 'businessId', label: 'Current Business ID:' },
  {
    type: 'select',
    key: 'datePickerMode',
    label: 'Date Picker Mode:',
    options: [
      { value: '', label: 'Default' },
      { value: 'dayRangePicker', label: 'Day Range Picker' },
      { value: 'monthPicker', label: 'Month Picker' },
    ],
  },
  {
    type: 'select',
    key: 'layerEnvironmentOverride',
    label: 'Layer Environment:',
    options: [
      { value: '', label: 'Site default' },
      ...LAYER_ENVIRONMENTS.map(environment => ({ value: environment, label: environment })),
    ],
  },
  { type: 'checkbox', key: 'bookkeepingOn', label: 'Bookkeeping On:' },
  { type: 'checkbox', key: 'showDescriptions', label: 'Show Transaction Memos:' },
  { type: 'checkbox', key: 'showReceiptUploads', label: 'Show Transaction Receipts:' },
  { type: 'checkbox', key: 'showInvoicesTab', label: 'Show Invoices Tab:' },
  { type: 'checkbox', key: 'showCustomerVendor', label: 'Show Customer/Vendor:' },
  { type: 'checkbox', key: 'defaultToMobile', label: 'Default to Mobile View:' },
]

const renderField = (
  field: FieldDef,
  values: DemoFormValues,
  onChange: DemoFormChange,
) => {
  switch (field.type) {
    case 'text':
      return (
        <input
          type='text'
          value={values[field.key]}
          onChange={e => onChange(field.key, e.target.value)}
          required={field.required}
        />
      )
    case 'checkbox':
      return (
        <input
          type='checkbox'
          checked={values[field.key]}
          onChange={e => onChange(field.key, e.target.checked)}
        />
      )
    case 'select':
      return (
        <select
          value={values[field.key] ?? ''}
          onChange={e =>
            onChange(
              field.key,
              (e.target.value || null) as DemoFormValues[typeof field.key],
            )
          }
        >
          {field.options.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )
  }
}

export const DemoFormFields = ({ values, onChange }: DemoFormFieldsProps) => (
  <>
    {FIELDS.map(field => (
      <LabeledField key={field.key} label={field.label}>
        {renderField(field, values, onChange)}
      </LabeledField>
    ))}
  </>
)
